import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

const CAL_ICON_PATH = 'M940.218182 107.054545h-209.454546V46.545455h-65.163636v60.50909H363.054545V46.545455H297.890909v60.50909H83.781818c-18.618182 0-32.581818 13.963636-32.581818 32.581819v805.236363c0 18.618182 13.963636 32.581818 32.581818 32.581818h861.090909c18.618182 0 32.581818-13.963636 32.581818-32.581818V139.636364c-4.654545-18.618182-18.618182-32.581818-37.236363-32.581819zM297.890909 172.218182V232.727273h65.163636V172.218182h307.2V232.727273h65.163637V172.218182h176.872727v204.8H116.363636V172.218182h181.527273zM116.363636 912.290909V442.181818h795.927273v470.109091H116.363636z'

const CLEAR_ICON_PATH = 'M810.005333 274.005333l-60.010666-60.010666L512 451.989333 274.005333 213.994667l-60.010666 60.010666L451.989333 512l-237.994666 237.994667 60.010666 60.010666L512 572.010667l237.994667 237.994666 60.010666-60.010666L572.010667 512z'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const pad = (n) => String(n).padStart(2, '0')
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
// Input shows DD/MM/YYYY (reference format); internal value stays YYYY-MM-DD
const displayFmt = (v) => {
  if (!v) return ''
  const [y, m, d] = v.split('-')
  return `${d}/${m}/${y}`
}
const parse = (v) => {
  if (!v) return null
  const d = new Date(`${v}T00:00:00`)
  return isNaN(d) ? null : d
}
const chunk = (arr, size) => {
  const out = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

// A "calendar" is the month a panel is showing — always the 1st, so month and
// year arithmetic can never roll over (31 Jan + 1 month is not 3 Mar here).
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const monthDiff = (a, b) => 12 * (b.getFullYear() - a.getFullYear()) + (b.getMonth() - a.getMonth())

// vue2-datepicker CalendarRange.updateCalendars, `calendarMinDiff` = 1 for a
// date picker: the two range calendars never show the same month. Whichever
// one the user did NOT move is the one that gives way.
const RANGE_MIN_GAP = 1
const linkCalendars = (cals, giveWay = 1) => {
  const next = [cals[0], cals[1]]
  const diff = monthDiff(next[0], next[1])
  if (diff < RANGE_MIN_GAP) {
    const gap = RANGE_MIN_GAP - diff
    const d = new Date(next[giveWay])
    d.setMonth(d.getMonth() + (giveWay === 0 ? -gap : gap))
    next[giveWay] = d
  }
  return next
}

// 6 fixed rows of 7, week starts Sunday — same grid as vue2-datepicker
const buildRows = (y, m) => {
  const firstDay = new Date(y, m, 1)
  const start = new Date(y, m, 1 - firstDay.getDay())
  const out = []
  for (let r = 0; r < 6; r++) {
    const cols = []
    for (let c = 0; c < 7; c++) {
      cols.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + r * 7 + c))
    }
    out.push(cols)
  }
  return out
}

const IconButton = ({ type, onClick }) => (
  <button type="button" className={`mx-btn mx-btn-text mx-btn-icon-${type}`} onClick={onClick}>
    <i className={`mx-icon-${type}`}></i>
  </button>
)

// vue2-datepicker CalendarPanel — one calendar that is a day grid, a month grid
// or a year grid. Markup is the reference's TableDate / TableMonth / TableYear
// templates (capture: scripts/vuevendor.js). One deviation: the month and year
// tables carry a <tbody>. Vue builds <table><tr> node by node and gets away
// with it; React logs a DOM-nesting error for it. No rule selects on it.
function CalendarPanel({ calendar, panel, onPanel, onCalendar, dateClass, onPick, onHover }) {
  const year = calendar.getFullYear()
  const month = calendar.getMonth()
  const moveMonth = (delta) => onCalendar(new Date(year, month + delta, 1))
  const moveYear = (delta) => onCalendar(new Date(year + delta, month, 1))

  if (panel === 'year') {
    const first = 10 * Math.floor(year / 10)
    const years = chunk(Array.from({ length: 10 }, (_, i) => first + i), 2)
    return (
      <div className="mx-calendar mx-calendar-panel-year">
        <div className="mx-calendar-header">
          <IconButton type="double-left" onClick={() => moveYear(-10)} />{' '}
          <IconButton type="double-right" onClick={() => moveYear(10)} />{' '}
          <span className="mx-calendar-header-label">
            <span>{first}</span>{' '}
            <span className="mx-calendar-decade-separator"></span>{' '}
            <span>{first + 9}</span>
          </span>
        </div>{' '}
        <div className="mx-calendar-content">
          <table className="mx-table mx-table-year">
            <tbody>
              {years.map((row, r) => (
                <tr key={r}>
                  {row.map((y) => (
                    <td
                      key={y}
                      data-year={y}
                      className={`cell${y === year ? ' active' : ''}`}
                      // picking a year moves on to the month grid, as the reference does
                      onClick={() => { onCalendar(new Date(y, month, 1)); onPanel('month') }}
                    >
                      <div>{y}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  if (panel === 'month') {
    const months = chunk(MONTHS.map((text, m) => ({ text, m })), 3)
    return (
      <div className="mx-calendar mx-calendar-panel-month">
        <div className="mx-calendar-header">
          <IconButton type="double-left" onClick={() => moveYear(-1)} />{' '}
          <IconButton type="double-right" onClick={() => moveYear(1)} />{' '}
          <span className="mx-calendar-header-label">
            <button type="button" className="mx-btn mx-btn-text" onClick={() => onPanel('year')}>{`\n        ${year}\n      `}</button>
          </span>
        </div>{' '}
        <div className="mx-calendar-content">
          <table className="mx-table mx-table-month">
            <tbody>
              {months.map((row, r) => (
                <tr key={r}>
                  {row.map(({ text, m }) => (
                    <td
                      key={m}
                      data-month={m}
                      className={`cell${m === month ? ' active' : ''}`}
                      onClick={() => { onCalendar(new Date(year, m, 1)); onPanel('date') }}
                    >
                      <div>{text}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-calendar mx-calendar-panel-date">
      <div className="mx-calendar-header">
        <IconButton type="double-left" onClick={() => moveYear(-1)} />{' '}
        <IconButton type="left" onClick={() => moveMonth(-1)} />{' '}
        <IconButton type="double-right" onClick={() => moveYear(1)} />{' '}
        <IconButton type="right" onClick={() => moveMonth(1)} />{' '}
        <span className="mx-calendar-header-label">
          <button type="button" className="mx-btn mx-btn-text mx-btn-current-month" onClick={() => onPanel('month')}>{`\n        ${MONTHS[month]}\n      `}</button>
          <button type="button" className="mx-btn mx-btn-text mx-btn-current-year" onClick={() => onPanel('year')}>{`\n        ${year}\n      `}</button>
        </span>
      </div>{' '}
      <div className="mx-calendar-content">
        <table className="mx-table mx-table-date">
          <thead>
            <tr>
              {' '}
              {WEEK_DAYS.map((d) => <th key={d}>{d}</th>)}
            </tr>
          </thead>{' '}
          <tbody>
            {buildRows(year, month).map((cols, r) => (
              <tr className="mx-date-row" key={r}>
                {' '}
                {cols.map((d, c) => {
                  const cls = dateClass(d, month)
                  return (
                    <td
                      key={c}
                      data-row-col={`${r},${c}`}
                      title={fmt(d)}
                      className={cls}
                      onClick={(e) => { e.stopPropagation(); onPick(d, cls.includes('disabled')) }}
                      onMouseEnter={() => onHover(fmt(d))}
                      onMouseLeave={() => onHover(null)}
                    >
                      <div>{d.getDate()}</div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// The open/close animation is vue2-datepicker's own <transition> — the rules
// are already in styles/app.css (.mx-zoom-in-down-*: scaleY + opacity, .3s).
// Same class sequence as the player site's components/MxDatePicker.jsx.
const ZOOM_MS = 300
const ZOOM_CLASS = {
  enter: ' mx-zoom-in-down-enter mx-zoom-in-down-enter-active',
  entering: ' mx-zoom-in-down-enter-active',
  leaving: ' mx-zoom-in-down-leave-active',
  'leave-to': ' mx-zoom-in-down-leave-active mx-zoom-in-down-leave-to',
}

// vue2-datepicker replica — same DOM as the reference site:
// closed: .mx-datepicker > .mx-input-wrapper > input.mx-input + i.mx-icon-calendar
// open:   .mx-datepicker-main.mx-datepicker-popup appended to body (absolute top/left)
function MxDatePicker({ value, onChange, disabled, disableFuture, minDate, name = 'date', range = false, rangeValue = [], onRangeChange, rootClassName, readonlyInput = true }) {
  const [open, setOpen] = useState(false)
  // `mounted` keeps the popup in the DOM through the leave transition, the way
  // Vue's <transition> does; `phase` drives the mx-zoom-in-down classes.
  const [mounted, setMounted] = useState(false)
  const [phase, setPhase] = useState('idle')
  const [pos, setPos] = useState({ top: 0, left: 0 })
  // range mode: first click picks the start, second click completes the range
  const [pendingFrom, setPendingFrom] = useState(null)
  const [hovered, setHovered] = useState(null)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const [rFrom, rTo] = range ? rangeValue : []
  // One calendar + one panel per grid on screen: [single] or [start, end].
  // Month of the current value, today when there is none.
  const initialCalendars = () => (range
    ? linkCalendars([startOfMonth(parse(rFrom) || today), startOfMonth(parse(rTo) || today)])
    : [startOfMonth(parse(value) || today)])
  const [calendars, setCalendars] = useState(initialCalendars)
  const [panels, setPanels] = useState(['date', 'date'])
  const rootRef = useRef(null)
  const popupRef = useRef(null)

  // Attributes set imperatively so the serialized DOM matches the Vue
  // reference exactly: attribute order, readonly="readonly"/disabled="disabled"
  // (React would emit readonly=""), and value as a property (no value attr)
  const setupInput = (el) => {
    if (!el) return
    // Rebuild from scratch every time — attribute insertion order defines the
    // serialized order, and React re-touches name/type on inputs during mount
    while (el.attributes.length) el.removeAttribute(el.attributes[0].name)
    el.setAttribute('name', name)
    el.setAttribute('type', 'text')
    el.setAttribute('autocomplete', 'off')
    if (!range && readonlyInput) el.setAttribute('readonly', 'readonly')
    if (disabled) el.setAttribute('disabled', 'disabled')
    el.setAttribute('placeholder', '')
    el.setAttribute('class', 'mx-input')
    el.value = range ? (rFrom && rTo ? `${displayFmt(rFrom)} ~ ${displayFmt(rTo)}` : '') : displayFmt(value)
  }

  // The reference's popup body is v-if'd, so every open starts over: day grid,
  // on the month of the current value.
  const openPopup = () => {
    if (disabled || open) return
    setCalendars(initialCalendars())
    setPanels(['date', 'date'])
    setPendingFrom(null)
    setHovered(null)
    setOpen(true)
  }

  const closePopup = () => {
    setOpen(false)
    setPendingFrom(null)
    setHovered(null)
  }

  /* Where the popup goes — vue2-datepicker's getRelativePosition, line for
     line, in its appendToBody form (offsets carry the page scroll):

       vertical    no room above AND none below → pinned to the viewport bottom
                   input in the top half         → below it
                   input in the bottom half      → ABOVE it
       horizontal  fits neither way              → viewport left edge
                   input centre in the left half → left edges aligned
                   otherwise                     → right edges aligned

     Always-below ran the grid off the bottom of a phone screen, always-left ran
     it off the right edge for a right-hand field. Size is measured, margins
     included (getPopupElementSize) — offsetHeight ignores the scaleY the enter
     transition is holding, so measuring mid-animation is safe. */
  const placePopup = () => {
    const el = popupRef.current
    if (!el || !rootRef.current) return
    const cs = window.getComputedStyle(el)
    const w = el.offsetWidth + (parseInt(cs.marginLeft, 10) || 0) + (parseInt(cs.marginRight, 10) || 0)
    const h = el.offsetHeight + (parseInt(cs.marginTop, 10) || 0) + (parseInt(cs.marginBottom, 10) || 0)
    const rect = rootRef.current.getBoundingClientRect()
    const dw = document.documentElement.clientWidth
    const dh = document.documentElement.clientHeight
    const offsetX = window.pageXOffset + rect.left
    const offsetY = window.pageYOffset + rect.top
    let left
    if (dw - rect.left < w && rect.right < w) left = offsetX - rect.left + 1
    else if (rect.left + rect.width / 2 <= dw / 2) left = offsetX
    else left = offsetX + rect.width - w
    let top
    // No room above or below: the reference pins the popup to the viewport's
    // bottom edge. The ONE deviation from its formula — it is never allowed to
    // rise above the field's own top. The two-calendar range popup is 542px,
    // so on a phone this branch is the normal case, and where the pin lands
    // depends on the phone: on a 402x874 it comes out 3px above the field's
    // top (the user's "expected" screenshot — it looks like it starts at the
    // field), on a 393x852 it came out 42px above it and covered the label and
    // the select over the field. The reference site does the same on that
    // phone; the user asked for the popup to start at the field. With the
    // clamp both phones show the "expected" picture. It can then run past the
    // viewport's bottom on a short screen — the page scrolls to it.
    if (rect.top <= h && dh - rect.bottom <= h) top = Math.max(offsetY + dh - rect.top - h, offsetY)
    else if (rect.top + rect.height / 2 <= dh / 2) top = offsetY + rect.height
    else top = offsetY - h
    setPos((p) => (p.top === top && p.left === left ? p : { top, left }))
  }

  // Before the first paint, so the popup is never seen at 0,0
  useLayoutEffect(() => {
    if (mounted) placePopup()
  }, [mounted])

  // …and it follows the input while open, as the reference's Popup does
  useEffect(() => {
    if (!open) return undefined
    let raf = 0
    const onMove = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(placePopup)
    }
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [open])

  // Vue's transition class sequence: `enter` + `enter-active` for one frame,
  // then `enter` drops and it grows; on leave, `leave-active` for a frame
  // before `leave-to` collapses it, and only then does it leave the DOM.
  useEffect(() => {
    let raf1, raf2, timer
    if (open) {
      setMounted(true)
      setPhase('enter')
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setPhase('entering'))
      })
      timer = setTimeout(() => setPhase('idle'), ZOOM_MS + 40)
    } else if (mounted) {
      setPhase('leaving')
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setPhase('leave-to'))
      })
      timer = setTimeout(() => { setPhase('idle'); setMounted(false) }, ZOOM_MS)
    }
    return () => {
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
      clearTimeout(timer)
    }
    // `mounted` must not retrigger this — only the open flag drives it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return
      if (popupRef.current && popupRef.current.contains(e.target)) return
      closePopup()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  const setPanel = (i, p) => setPanels((prev) => prev.map((x, k) => (k === i ? p : x)))

  // Range: moving one calendar pushes the OTHER one out of its way
  const setCalendar = (i, d) => setCalendars((prev) => {
    if (!range) return [d]
    return i === 0 ? linkCalendars([d, prev[1]], 1) : linkCalendars([prev[0], d], 0)
  })

  const pickCell = (d, isDisabled) => {
    if (isDisabled) return
    if (range) {
      const v = fmt(d)
      if (!pendingFrom) {
        setPendingFrom(v)
        return
      }
      let a = pendingFrom
      let b = v
      if (b < a) [a, b] = [b, a]
      if (onRangeChange) onRangeChange(a, b)
      closePopup()
      return
    }
    if (onChange) onChange(fmt(d))
    closePopup()
  }

  // Only a range pick in progress cares where the pointer is
  const hoverCell = (v) => { if (range && pendingFrom) setHovered(v) }

  /* CalendarPanel.getDateClasses + CalendarRange.getRangeClasses, same order
     the reference serializes them in: today, not-current-month, then the state
     (disabled beats active), then the range class.
       · `active` is NOT put on a not-current-month cell
       · a disabled or active cell never gets a range class
       · mid-pick, days between the first click and the pointer are
         `hover-in-range` (the reference stylesheet has no rule for it — the
         class is there for parity, it paints nothing) */
  const dateClass = (d, panelMonth) => {
    const v = fmt(d)
    const other = d.getMonth() !== panelMonth
    let cls = 'cell'
    if (v === fmt(today)) cls += ' today'
    if (other) cls += ' not-current-month'
    // minDate is 'YYYY-MM-DD' like every other value here, so strings compare
    const isDisabled = (!!disableFuture && d.getTime() > today.getTime()) || (!!minDate && v < minDate)
    const picked = range ? (pendingFrom ? [pendingFrom] : [rFrom, rTo].filter(Boolean)) : (value ? [value] : [])
    const isActive = picked.includes(v)
    if (isDisabled) cls += ' disabled'
    else if (isActive && !other) cls += ' active'
    if (!range || isDisabled || (isActive && !other)) return cls
    const between = (a, b) => (a < b ? v > a && v < b : v > b && v < a)
    if (picked.length === 2 && between(picked[0], picked[1])) cls += ' in-range'
    else if (picked.length === 1 && hovered && between(picked[0], hovered)) cls += ' hover-in-range'
    return cls
  }

  const renderPanel = (i) => (
    <CalendarPanel
      key={i}
      calendar={calendars[i]}
      panel={panels[i]}
      onPanel={(p) => setPanel(i, p)}
      onCalendar={(d) => setCalendar(i, d)}
      dateClass={dateClass}
      onPick={pickCell}
      onHover={hoverCell}
    />
  )

  return (
    <div className={rootClassName || `mx-datepicker${disabled ? ' disabled' : ''}`} ref={rootRef}>
      {/* Opens on mousedown / touchstart — a deliberate deviation. The reference
          opens from `click` on this wrapper and from `focus` on the input
          (capture vuevendor.js: on:{…click:this.openPopup},
          handleInputFocus -> openPopup). We have no focus path (the input is
          readonly on most pages), and click alone fails on iOS: a first tap on
          something whose :hover reveals an element is spent on the hover and
          never becomes a click, and app.css has exactly that
          (.mx-input-wrapper:hover .mx-icon-clear { display: block }) — the
          first tap only swapped the calendar icon for the ×, the popup needed
          a second one (phone recording, 2026-09-21). touchstart is not subject
          to that. Both events fire on a touch; openPopup ignores the second. */}
      <div className="mx-input-wrapper" onMouseDown={openPopup} onTouchStart={openPopup}>
        <input ref={setupInput} />
        {/* clear (x) — CSS shows it on hover instead of the calendar icon */}
        {!disabled && (range ? (rFrom && rTo) : !!value) && (
          <i
            className="mx-icon-clear"
            // keep the press away from the wrapper, or clearing would open the popup first
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              closePopup()
              if (range) {
                if (onRangeChange) onRangeChange('', '')
              } else if (onChange) {
                onChange('')
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
              <path d={CLEAR_ICON_PATH}></path>
            </svg>
          </i>
        )}
        <i className="mx-icon-calendar">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1em" height="1em">
            <path d={CAL_ICON_PATH}></path>
          </svg>
        </i>
      </div>
      {mounted && createPortal(
        <div
          ref={popupRef}
          className={`mx-datepicker-main mx-datepicker-popup${ZOOM_CLASS[phase] || ''}`}
          style={{ position: 'absolute', top: `${pos.top}px`, left: `${pos.left}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mx-datepicker-content">
            <div className="mx-datepicker-body">
              {range && calendars.length === 2 ? (
                <div className="mx-range-wrapper">
                  {renderPanel(0)}
                  {renderPanel(1)}
                </div>
              ) : (
                renderPanel(0)
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default MxDatePicker
